import { ColumnDataTypes, FieldEntitiesType } from 'core/constants/report';
import {
  AggExprTypes,
  ArithmeticExprTypes,
  ArrayFnExprTypes,
  CompExprTypes,
  ConditionExprTypes,
  ConfigExprNamespace,
  DateDiffFnExprTypes,
  DateExprTypes,
  DateFnExprTypes,
  ExpressionSubTypes,
  ExpressionTypes,
  KeywordExprTypes,
  LogicalExprTypes,
  SortDirection,
  StringFnExprTypes,
  JSONFnExprTypes,
  CastFnExprTypes,
  TenantSettingKey,
  TimeDuration, usersPrefix,
  WindowFnExprTypes,
} from 'core/constants/report-expressions';
import { DateBinningFormatters } from 'core/constants/formatters';
import { isValidArrayAndNotEmpty } from 'components/feature/Report/ReportSidebar/common/helpers';

/**
 * Tree-like representation of an expression. Expressions have a type T and may
 * have a subtype S if T has any. `Part` holds details of the expression (like
 * column name, data type, etc.) and its type is derived from T and S (see
 * `IExpressionPart`).
 *
 * Example usage:
 * ```
 * newSumExpr(
 *   newColumnExpr('Activity Revenue', ColumnDataTypes.IntType),
 *   newEqualExpr(
 *     newColumnExpr('Activity', ColumnDataTypes.Int64Type),
 *     newConstantExpr(123, ColumnDataTypes.IntType),
 *   ),
 * ),
 * ```
 */

export interface IExpression<
  T extends ExpressionTypes = ExpressionTypes,
  S extends ExpressionSubTypes<T> = ExpressionSubTypes<T>
> {
  Type: T;
  SubType: S;
  Part: IExpressionPart<T, S>;
}

/**
 * This type resolves the given expression type and subtype to a part interface.
 *
 * This makes it easy to know which part is for which type, and as a bonus
 * which subtype belongs to which type.
 * For instance, `IExpressionPart<Aggregation, Sum>` resolves to `ISumExprPart`
 * and `IExpressionPart<Aggregation, LessThan>` is a compile-time error.
 * Every subtype and interface have one to one relation
 */
export type IExpressionPart<
  T extends ExpressionTypes,
  S extends ExpressionSubTypes<T>
> = T extends ExpressionTypes.Condition
  ? IConditionalExprPart
  : T extends ExpressionTypes.Column
  ? IColumnExprPart
  : T extends ExpressionTypes.Constant
  ? IConstantExprPart
  : T extends ExpressionTypes.CustomFilterVariable
  ? ICustomFilterVariableExprPart
  : T extends ExpressionTypes.Keyword ? IKeywordExprPart
  : T extends ExpressionTypes.Config ? IConfigExprPart
  : // AggExpr
  S extends AggExprTypes.Sum
  ? ISumExprPart
  : S extends AggExprTypes.SumIf
  ? ISumIfExprPart
  : S extends AggExprTypes.Max
  ? IMaxExprPart
  : S extends AggExprTypes.MaxIf
  ? IMaxIfExprPart
  : S extends AggExprTypes.Min
  ? IMinExprPart
  : S extends AggExprTypes.MinIf
  ? IMinIfExprPart
  : S extends AggExprTypes.Avg
  ? IAvgExprPart
  : S extends AggExprTypes.AvgIf
  ? IAvgIfExprPart
  : S extends AggExprTypes.Count
  ? ICountExprPart
  : S extends AggExprTypes.CountIf
  ? ICountIfExprPart
  : S extends AggExprTypes.CountDistinct
  ? ICountDistinctExprPart
  : S extends AggExprTypes.CountDistinctIf
  ? ICountDistinctIfExprPart
  : S extends AggExprTypes.ArgMax
  ? IArgMaxExprPart
  : S extends AggExprTypes.ArgMin
  ? IArgMinExprPart
  : S extends AggExprTypes.ArgMaxIf
  ? IArgMaxIfExprPart
  : S extends AggExprTypes.ArgMinIf
  ? IArgMinIfExprPart
  : S extends AggExprTypes.Any
  ? IAnyExprPart
  : S extends AggExprTypes.AnyHeavy
  ? IAnyHeavyExprPart
  : S extends AggExprTypes.AnyLast
  ? IAnyLastExprPart
  : S extends AggExprTypes.GroupConcat
  ? IGroupConcatExprPart
  : S extends AggExprTypes.GroupConcatDistinct
  ? IGroupConcatDistinctExprPart
  : S extends WindowFnExprTypes.RowNumber
  ? IRowNumberExprPart
  : S extends WindowFnExprTypes.Rank
  ? IRankExprPart
  : S extends WindowFnExprTypes.FirstValue
  ? IFirstValueExprPart
  : S extends WindowFnExprTypes.LastValue
  ? ILastValueExprPart
  : S extends WindowFnExprTypes.Lead
  ? ILeadExprPart
  : S extends WindowFnExprTypes.Lag
  ? ILagExprPart
  : S extends StringFnExprTypes.Position
  ? IPositionExprPart
  : S extends StringFnExprTypes.Concat
  ? IConcatExprPart
  : S extends StringFnExprTypes.RegexpExtract
  ? IRegexpExtractExprPart
  : S extends StringFnExprTypes.Replace
  ? IReplaceExprPart
  : S extends StringFnExprTypes.ReplaceRegexp
  ? IReplaceRegexpExprPart
  : S extends JSONFnExprTypes.JSONExtract
  ? IJSONExtractExprPart
  : S extends ArithmeticExprTypes.Plus ? IPlusExprPart
  : S extends ArithmeticExprTypes.Minus ? IMinusExprPart
  : S extends ArithmeticExprTypes.Multiply ? IMultiplyExprPart
  : S extends ArithmeticExprTypes.Divide ? IDivideExprPart
  : S extends CompExprTypes ? IComparisonExprPart<S>
  : S extends LogicalExprTypes.And ? IAndExprPart
  : S extends LogicalExprTypes.Or ? IOrExprPart
  : S extends LogicalExprTypes.Not ? INotExprPart
  : S extends LogicalExprTypes.IsNull ? IIsNullExprPart
  : S extends DateBinningFormatters ? IDateConvertExprPart
  : S extends DateFnExprTypes ? IDateFnExprPart
  : S extends DateDiffFnExprTypes ? IDateDiffFnExprPart
  : S extends ArrayFnExprTypes.ArrayJoinExpr ? IArrayJoinExprPart
  : S extends ArrayFnExprTypes.ArrayIndexOfExpr ? IArrayIndexOfExprPart
  : S extends CastFnExprTypes.Cast ? ICastExprPart
  : S extends CastFnExprTypes.AccurateCastOrNull ? ICastExprPart
  : S extends CastFnExprTypes.AccurateCastOrDefault ? IAccurateCastOrDefaultExprPart
  : never;

export interface IConditionalExprPart {
  Condition: IExpression;
  ThenPart: IExpression;
  ElsePart: IExpression;
}

// #region Helper Types
interface IConditionPartBase {
  Condition: IExpression;
}
type IAggExpr<S extends ExpressionSubTypes<ExpressionTypes.Aggregation>> =
  IExpression<ExpressionTypes.Aggregation, S>;
type IArithmeticExpr<T extends ExpressionSubTypes<ExpressionTypes.Arithmetic> = ArithmeticExprTypes> =
  IExpression<ExpressionTypes.Arithmetic, T>;
type ICompExpr<S extends ExpressionSubTypes<ExpressionTypes.Comparison>> =
  IExpression<ExpressionTypes.Comparison, S>;
type ILogicalExpr<S extends LogicalExprTypes> = IExpression<ExpressionTypes.Logical, S>;
// #endregion

//#region Column & Constant
export interface IColumnExprPart {
  Column: {
    SchemaName: string;
    DisplayName: string;
    DataType: ColumnDataTypes;
  };
  PrependTableName?: boolean;
  TableNameOverride?: string;
}
export const newColumnExpr = (name: string, schemaName: string, dataType: ColumnDataTypes, prefix?: string, entity?: string, isSwitchField?: boolean) => {
  let prefix_ = prefix;
  if (entity === FieldEntitiesType.User && !prefix && !isSwitchField) { // user field columns require 'user' as prefix
    prefix_ = usersPrefix;
  }
  return ({
    Type: ExpressionTypes.Column,
    Part: {
      Column: {
        SchemaName: schemaName,
        DisplayName: name,
        DataType: dataType,
      },
      PrependTableName: prefix_ ? true : undefined,
      TableNameOverride: prefix_ || undefined,
    },
  } as IExpression<ExpressionTypes.Column>);
};

type ConstantValueType<T extends ColumnDataTypes> =
  T extends ColumnDataTypes.String
    ? string
    : T extends ColumnDataTypes.Time
    ? string
    : T extends ColumnDataTypes.DateTime
    ? string
    : T extends ColumnDataTypes.Default
    ? number
    : T extends ColumnDataTypes.Int
    ? number
    : T extends ColumnDataTypes.Int64
    ? number
    : T extends ColumnDataTypes.Decimal
    ? number
    : T extends ColumnDataTypes.Boolean
    ? boolean
    : T extends ColumnDataTypes.StringArray
    ? string[]
    : T extends ColumnDataTypes.Int64Array
    ? number[]
    : never;

export interface IConstantExprPart<
  T extends ColumnDataTypes = ColumnDataTypes
> {
  Value: ConstantValueType<T>;
  DataType: T;
}
export const newConstantExpr = <T extends ColumnDataTypes>(
  value: ConstantValueType<T>,
  dataType: T,
) => ({
    Type: ExpressionTypes.Constant,
    SubType: null,
    Part: { Value: value, DataType: dataType },
  } as IExpression<ExpressionTypes.Constant>);

/**
 * Placeholder node for a custom filter variable. Sits as the RHS of a ComparisonExpr in the
 * saved measure expression. `Value` matches a `CustomFilterDefinition.Label` (UseVar=true); the
 * backend replaces this node at fetch time using that definition's DataType/Operator. The
 * `DataType` here is informational only.
 */
export interface ICustomFilterVariableExprPart {
  Value: string;
  DataType: ColumnDataTypes;
}
export const newCustomFilterVariableExpr = (
  label: string,
  dataType: ColumnDataTypes,
) => ({
  Type: ExpressionTypes.CustomFilterVariable,
  SubType: null,
  Part: { Value: label, DataType: dataType },
} as IExpression<ExpressionTypes.CustomFilterVariable>);

export interface IKeywordExprPart {}
export const newNullExpr = (): IExpression<ExpressionTypes.Keyword, KeywordExprTypes.Null> => ({
  Type: ExpressionTypes.Keyword,
  SubType: KeywordExprTypes.Null,
  Part: {},
});
//#endregion

//#region AggExpr
export interface IWindow {
  PartitionBy: IExpression[];
  OrderBy?: IOrderBy[];
}

export interface ISumExprPart {
  ExpressionToSum: IExpression;
  Window?: IWindow;
}
export interface ISumIfExprPart extends ISumExprPart, IConditionPartBase {}
export const newSumExpr = (summand: IExpression, comparison?: IExpression, partitionFields?: IExpression[] | null) => ({
  Type: ExpressionTypes.Aggregation,
  SubType: comparison ? AggExprTypes.SumIf : AggExprTypes.Sum,
  Part: {
    ExpressionToSum: summand,
    ...(comparison ? { Condition: comparison } : {}),
    ...(partitionFields ? { Window: { PartitionBy: isValidArrayAndNotEmpty(partitionFields) ? partitionFields : [] } } : {}),
  },
} as IAggExpr<AggExprTypes.SumIf | AggExprTypes.Sum>);

export interface IAnyExprPart { Expression: IExpression }
export const newAnyExpr = (expr: IExpression) => ({
  Type: ExpressionTypes.Aggregation,
  SubType: AggExprTypes.Any,
  Part: { Expression: expr },
} as IAggExpr<AggExprTypes.Any>);

export interface IAnyHeavyExprPart { Expression: IExpression }
export const newAnyHeavyExpr = (expr: IExpression) => ({
  Type: ExpressionTypes.Aggregation,
  SubType: AggExprTypes.AnyHeavy,
  Part: { Expression: expr },
} as IAggExpr<AggExprTypes.AnyHeavy>);

export interface IAnyLastExprPart { Expression: IExpression }
export const newAnyLastExpr = (expr: IExpression) => ({
  Type: ExpressionTypes.Aggregation,
  SubType: AggExprTypes.AnyLast,
  Part: { Expression: expr },
} as IAggExpr<AggExprTypes.AnyLast>);

export interface IGroupConcatExprPart {
  ExpressionToConcat: IExpression;
  Separator?: string;
  Window?: IWindow;
}
// GroupConcatDistinct shares the same wire shape — only the SubType differs.
export interface IGroupConcatDistinctExprPart extends IGroupConcatExprPart {}
export const newGroupConcatExpr = (
  field: IExpression,
  opts: {
    distinct?: boolean;
    separator?: string;
    partitionFields?: IExpression[] | null;
  } = {},
) => ({
  Type: ExpressionTypes.Aggregation,
  SubType: opts.distinct ? AggExprTypes.GroupConcatDistinct : AggExprTypes.GroupConcat,
  Part: {
    ExpressionToConcat: field,
    ...(opts.separator ? { Separator: opts.separator } : {}),
    ...(opts.partitionFields ? { Window: { PartitionBy: isValidArrayAndNotEmpty(opts.partitionFields) ? opts.partitionFields : [] } } : {}),
  },
} as IAggExpr<AggExprTypes.GroupConcat | AggExprTypes.GroupConcatDistinct>);

export interface IMaxExprPart {
  MaxOf: IExpression;
  Window?: IWindow;
}
export interface IMaxIfExprPart extends IConditionPartBase {
  ExpressionToMax: IExpression;
  Window?: IWindow;
}
export const newMaxExpr = (exprToMax: IExpression, comparison?: IExpression, partitionFields?: IExpression[] | null) => ({
  Type: ExpressionTypes.Aggregation,
  SubType: comparison ? AggExprTypes.MaxIf : AggExprTypes.Max,
  Part: {
    ...(comparison ? { ExpressionToMax: exprToMax, Condition: comparison } : { MaxOf: exprToMax }),
    ...(partitionFields ? { Window: { PartitionBy: isValidArrayAndNotEmpty(partitionFields) ? partitionFields : [] } } : {}),
  },
} as IAggExpr<AggExprTypes.Max | AggExprTypes.MaxIf>);

export interface IMinExprPart {
  MinOf: IExpression;
  Window?: IWindow;
}
export interface IMinIfExprPart extends IConditionPartBase {
  ExpressionToMin: IExpression;
  Window?: IWindow;
}
export const newMinExpr = (exprToMin: IExpression, comparison?: IExpression, partitionFields?: IExpression[] | null) => ({
  Type: ExpressionTypes.Aggregation,
  SubType: comparison ? AggExprTypes.MinIf : AggExprTypes.Min,
  Part: {
    ...(comparison ? { ExpressionToMin: exprToMin, Condition: comparison } : { MinOf: exprToMin }),
    ...(partitionFields ? { Window: { PartitionBy: isValidArrayAndNotEmpty(partitionFields) ? partitionFields : [] } } : {}),
  },
} as IAggExpr<AggExprTypes.Min | AggExprTypes.MinIf>);

export interface IAvgExprPart {
  ExpressionToAvg: IExpression;
  Window?: IWindow;
}
export interface IAvgIfExprPart extends IAvgExprPart, IConditionPartBase {}
export const newAvgExpr = (exprToAvg: IExpression, comparison?: IExpression, partitionFields?: IExpression[] | null) => ({
  Type: ExpressionTypes.Aggregation,
  SubType: comparison ? AggExprTypes.AvgIf : AggExprTypes.Avg,
  Part: {
    ExpressionToAvg: exprToAvg,
    ...(comparison ? { Condition: comparison } : {}),
    ...(partitionFields ? { Window: { PartitionBy: isValidArrayAndNotEmpty(partitionFields) ? partitionFields : [] } } : {}),
  },
} as IAggExpr<AggExprTypes.Avg | AggExprTypes.AvgIf>);

export interface ICountExprPart {
  ElementToCount: IExpression;
  Window?: IWindow;
}
export interface ICountIfExprPart extends IConditionPartBase {
  Window?: IWindow;
}
export interface ICountDistinctExprPart {
  ExpressionToCount: IExpression;
  Window?: IWindow;
}
export interface ICountDistinctIfExprPart
  extends ICountDistinctExprPart,
    IConditionPartBase {}
export const newCountExpr = (
  exprToCount: IExpression | null,
  distinct?: boolean,
  comparison?: IExpression,
  partitionFields?: IExpression[],
) => {
  if (distinct) {
    return {
      Type: ExpressionTypes.Aggregation,
      SubType: comparison
        ? AggExprTypes.CountDistinctIf
        : AggExprTypes.CountDistinct,
      Part: {
        ExpressionToCount: exprToCount,
        ...(comparison ? { Condition: comparison } : {}),
        ...(partitionFields ? { Window: { PartitionBy: isValidArrayAndNotEmpty(partitionFields) ? partitionFields : [] } } : {}),
      },
    } as IAggExpr<AggExprTypes.CountDistinct | AggExprTypes.CountDistinctIf>;
  }

  return {
    Type: ExpressionTypes.Aggregation,
    SubType: comparison ? AggExprTypes.CountIf : AggExprTypes.Count,
    Part: {
      ...(comparison ? { Condition: comparison } : { ElementToCount: exprToCount }),
      ...(partitionFields ? { Window: { PartitionBy: isValidArrayAndNotEmpty(partitionFields) ? partitionFields : [] } } : {}),
    },
  } as IAggExpr<AggExprTypes.Count | AggExprTypes.CountIf>;
};

export interface IArgMaxExprPart {
  Target: IExpression;
  Selector: IExpression;
}
export const newArgMaxExpr = (targetField: IExpression, selectorField: IExpression) => ({
  Type: ExpressionTypes.Aggregation,
  SubType: AggExprTypes.ArgMax,
  Part: { Target: targetField, Selector: selectorField },
} as IAggExpr<AggExprTypes.ArgMax>);

export interface IArgMinExprPart {
  Target: IExpression;
  Selector: IExpression;
}
export const newArgMinExpr = (targetField: IExpression, selectorField: IExpression) => ({
  Type: ExpressionTypes.Aggregation,
  SubType: AggExprTypes.ArgMin,
  Part: { Target: targetField, Selector: selectorField },
} as IAggExpr<AggExprTypes.ArgMin>);

export interface IArgMaxIfExprPart {
  Target: IExpression;
  Selector: IExpression;
  Condition: IExpression;
}
export const newArgMaxIfExpr = (targetField: IExpression, selectorField: IExpression, condition: IExpression) => ({
  Type: ExpressionTypes.Aggregation,
  SubType: AggExprTypes.ArgMaxIf,
  Part: { Target: targetField, Selector: selectorField, Condition: condition },
} as IAggExpr<AggExprTypes.ArgMaxIf>);

export interface IArgMinIfExprPart {
  Target: IExpression;
  Selector: IExpression;
  Condition: IExpression;
}
export const newArgMinIfExpr = (targetField: IExpression, selectorField: IExpression, condition: IExpression) => ({
  Type: ExpressionTypes.Aggregation,
  SubType: AggExprTypes.ArgMinIf,
  Part: { Target: targetField, Selector: selectorField, Condition: condition },
} as IAggExpr<AggExprTypes.ArgMinIf>);

export interface IOrderByItem {
  Field: IExpression;
  Direction: SortDirection;
}

export interface IOrderBy {
  Expression: IExpression;
  Descending?: boolean;
}

export interface IRowNumberExprPart {
  WindowParts: IWindow;
}

export interface IRankExprPart {
  WindowParts: IWindow;
}

export interface IFirstValueExprPart {
  Expression: IExpression;
  WindowParts: IWindow;
}

export interface ILastValueExprPart {
  Expression: IExpression;
  WindowParts: IWindow;
}

export interface ILeadExprPart {
  Expression: IExpression;
  Offset: number;
  WindowParts: IWindow;
}

export interface ILagExprPart {
  Expression: IExpression;
  Offset: number;
  WindowParts: IWindow;
}

type IWindowFnExpr<S extends ExpressionSubTypes<ExpressionTypes.WindowFn>> =
  IExpression<ExpressionTypes.WindowFn, S>;

export const newRowNumberExpr = (partitionFields?: IExpression[], orderByItems?: IOrderByItem[]) => {
  // Convert IOrderByItem[] to IOrderBy[]
  const orderByExprs: IOrderBy[] | undefined = orderByItems?.map((item) => ({
    Expression: item.Field,
    Descending: item.Direction === SortDirection.DESC,
  }));

  return {
    Type: ExpressionTypes.WindowFn,
    SubType: WindowFnExprTypes.RowNumber,
    Part: {
      WindowParts: {
        ...(partitionFields && partitionFields.length > 0 ? { PartitionBy: partitionFields } : {}),
        ...(orderByExprs && orderByExprs.length > 0 ? { OrderBy: orderByExprs } : {}),
      },
    },
  } as IWindowFnExpr<WindowFnExprTypes.RowNumber>;
};

export const newRankExpr = (partitionFields?: IExpression[], orderByItems?: IOrderByItem[]) => {
  // Convert IOrderByItem[] to IOrderBy[]
  const orderByExprs: IOrderBy[] | undefined = orderByItems?.map((item) => ({
    Expression: item.Field,
    Descending: item.Direction === SortDirection.DESC,
  }));

  return {
    Type: ExpressionTypes.WindowFn,
    SubType: WindowFnExprTypes.Rank,
    Part: {
      WindowParts: {
        ...(partitionFields && partitionFields.length > 0 ? { PartitionBy: partitionFields } : {}),
        ...(orderByExprs && orderByExprs.length > 0 ? { OrderBy: orderByExprs } : {}),
      },
    },
  } as IWindowFnExpr<WindowFnExprTypes.Rank>;
};

export const newFirstValueExpr = (firstValueExpr: IExpression, partitionFields?: IExpression[], orderByItems?: IOrderByItem[]) => {
  const orderByExprs: IOrderBy[] | undefined = orderByItems?.map((item) => ({
    Expression: item.Field,
    Descending: item.Direction === SortDirection.DESC,
  }));

  return {
    Type: ExpressionTypes.WindowFn,
    SubType: WindowFnExprTypes.FirstValue,
    Part: {
      Expression: firstValueExpr,
      WindowParts: {
        ...(partitionFields && partitionFields.length > 0 ? { PartitionBy: partitionFields } : {}),
        ...(orderByExprs && orderByExprs.length > 0 ? { OrderBy: orderByExprs } : {}),
      },
    },
  } as IWindowFnExpr<WindowFnExprTypes.FirstValue>;
};

export const newLastValueExpr = (lastValueExpr: IExpression, partitionFields?: IExpression[], orderByItems?: IOrderByItem[]) => {
  const orderByExprs: IOrderBy[] | undefined = orderByItems?.map((item) => ({
    Expression: item.Field,
    Descending: item.Direction === SortDirection.DESC,
  }));

  return {
    Type: ExpressionTypes.WindowFn,
    SubType: WindowFnExprTypes.LastValue,
    Part: {
      Expression: lastValueExpr,
      WindowParts: {
        ...(partitionFields && partitionFields.length > 0 ? { PartitionBy: partitionFields } : {}),
        ...(orderByExprs && orderByExprs.length > 0 ? { OrderBy: orderByExprs } : {}),
      },
    },
  } as IWindowFnExpr<WindowFnExprTypes.LastValue>;
};

export const newLeadExpr = (leadExpr: IExpression, offset: number, partitionFields?: IExpression[], orderByItems?: IOrderByItem[]) => {
  const orderByExprs: IOrderBy[] | undefined = orderByItems?.map((item) => ({
    Expression: item.Field,
    Descending: item.Direction === SortDirection.DESC,
  }));

  return {
    Type: ExpressionTypes.WindowFn,
    SubType: WindowFnExprTypes.Lead,
    Part: {
      Expression: leadExpr,
      Offset: offset,
      WindowParts: {
        ...(partitionFields && partitionFields.length > 0 ? { PartitionBy: partitionFields } : {}),
        ...(orderByExprs && orderByExprs.length > 0 ? { OrderBy: orderByExprs } : {}),
      },
    },
  } as IWindowFnExpr<WindowFnExprTypes.Lead>;
};

export const newLagExpr = (lagExpr: IExpression, offset: number, partitionFields?: IExpression[], orderByItems?: IOrderByItem[]) => {
  const orderByExprs: IOrderBy[] | undefined = orderByItems?.map((item) => ({
    Expression: item.Field,
    Descending: item.Direction === SortDirection.DESC,
  }));

  return {
    Type: ExpressionTypes.WindowFn,
    SubType: WindowFnExprTypes.Lag,
    Part: {
      Expression: lagExpr,
      Offset: offset,
      WindowParts: {
        ...(partitionFields && partitionFields.length > 0 ? { PartitionBy: partitionFields } : {}),
        ...(orderByExprs && orderByExprs.length > 0 ? { OrderBy: orderByExprs } : {}),
      },
    },
  } as IWindowFnExpr<WindowFnExprTypes.Lag>;
};
//#endregion

//#region Arithmetic
export interface IPlusExprPart { ItemsToAdd: IExpression[] }
export const newPlusExpr = (...items: IExpression[]): IArithmeticExpr<ArithmeticExprTypes.Plus> => ({
  Type: ExpressionTypes.Arithmetic,
  SubType: ArithmeticExprTypes.Plus,
  Part: { ItemsToAdd: items },
});

export interface IMinusExprPart { Minuend: IExpression, Subtrahend: IExpression }
export const newMinusExpr = (...items: IExpression[]): IArithmeticExpr<ArithmeticExprTypes.Minus> => (
  items.slice(2).reduce<IArithmeticExpr<ArithmeticExprTypes.Minus>>(
    (prev, curr) => newMinusBinaryExpr(prev, curr) as IArithmeticExpr<ArithmeticExprTypes.Minus>,
    newMinusBinaryExpr(items[0] ?? newConstantExpr(0, ColumnDataTypes.Int), items[1] ?? newConstantExpr(0, ColumnDataTypes.Int)),
  )
);
export const newMinusBinaryExpr = (minuend: IExpression, subtrahend: IExpression): IArithmeticExpr<ArithmeticExprTypes.Minus> => ({
  Type: ExpressionTypes.Arithmetic,
  SubType: ArithmeticExprTypes.Minus,
  Part: { Minuend: minuend, Subtrahend: subtrahend },
});

export interface IMultiplyExprPart { Multiplicand: IExpression, Multiplier: IExpression }
export const newMultiplyExpr = (multiplicand: IExpression, multiplier: IExpression): IArithmeticExpr<ArithmeticExprTypes.Multiply> => ({
  Type: ExpressionTypes.Arithmetic,
  SubType: ArithmeticExprTypes.Multiply,
  Part: { Multiplicand: multiplicand, Multiplier: multiplier },
});

export interface IDivideExprPart { Dividend: IExpression, Divisor: IExpression }
export const newDivideExpr = (dividend: IExpression, divisor: IExpression): IArithmeticExpr<ArithmeticExprTypes.Divide> => ({
  Type: ExpressionTypes.Arithmetic,
  SubType: ArithmeticExprTypes.Divide,
  Part: { Dividend: dividend, Divisor: divisor },
});
//#endregion

//#region CompExpr
export interface IComparisonExprPart<C extends CompExprTypes> {
  LHS: IExpression;
  RHS: IExpression;
  Operator: C;
}
export const newComparisonExpr = <C extends CompExprTypes>(lhs: IExpression, rhs: IExpression, operator: C) => ({
  Type: ExpressionTypes.Comparison,
  SubType: operator,
  Part: { LHS: lhs, RHS: rhs, Operator: operator },
} as ICompExpr<C>);
//#endregion

//#region LogicalExpr
export interface IAndExprPart { Conditions: Array<IExpression> }
export interface IOrExprPart { Conditions: Array<IExpression> }
export interface INotExprPart { Expression: IExpression }
export interface IIsNullExprPart { Expression: IExpression }
export const newAndExpr = (conditions: IExpression[]): IExpression<ExpressionTypes.Logical, LogicalExprTypes.And> => ({
  Type: ExpressionTypes.Logical, SubType: LogicalExprTypes.And, Part: { Conditions: conditions },
});
export const newOrExpr = (conditions: IExpression[]): IExpression<ExpressionTypes.Logical, LogicalExprTypes.Or> => ({
  Type: ExpressionTypes.Logical, SubType: LogicalExprTypes.Or, Part: { Conditions: conditions },
});
export const newNotExpr = (expr: IExpression): IExpression<ExpressionTypes.Logical, LogicalExprTypes.Not> => ({
  Type: ExpressionTypes.Logical, SubType: LogicalExprTypes.Not, Part: { Expression: expr },
});
export const newIsNullExpr = (expr: IExpression): IExpression<ExpressionTypes.Logical, LogicalExprTypes.IsNull> => ({
  Type: ExpressionTypes.Logical, SubType: LogicalExprTypes.IsNull, Part: { Expression: expr },
});
//#endregion

//#region DateExpr
type IDateExpr<S extends ExpressionSubTypes<ExpressionTypes.Date>> =
  IExpression<ExpressionTypes.Date, S>;
export interface IDateConvertExprPart {
  DateTime: IExpression;
}
export interface IDateFnExprPart {
  DateTime?: IExpression;
  DurationType?: TimeDuration;
  Duration?: number;
}
export interface IDateDiffFnExprPart {
  LHS: IExpression;
  RHS: IExpression;
  Unit?: TimeDuration;
  Bounded?: boolean;
}

export const newDateExpr = (
  binningType: DateBinningFormatters,
  dateExpr: IExpression,
): IDateExpr<DateBinningFormatters> => ({
  Type: ExpressionTypes.Date,
  SubType: binningType,
  Part: {
    DateTime: dateExpr,
  },
});

export const newDateFnExpr = (fnType: DateFnExprTypes, dateExpr: IExpression, duration: number, durationType: TimeDuration)
  : IDateExpr<DateFnExprTypes> => ({
  Type: ExpressionTypes.Date,
  SubType: fnType,
  Part: {
    DateTime: dateExpr,
    DurationType: durationType,
    Duration: duration,
  },
});

export const newDateNowExpr = (): IDateExpr<DateFnExprTypes> => ({ Type: ExpressionTypes.Date, SubType: DateFnExprTypes.CurrentDateTime, Part: {} });

export const newDateDiffExpr = (lhs: IExpression, rhs: IExpression, Unit: TimeDuration, bounded?: boolean)
  : IDateExpr<DateDiffFnExprTypes> => ({
  Type: ExpressionTypes.Date,
  SubType: DateDiffFnExprTypes.DifferenceExpr,
  Part: {
    LHS: lhs,
    RHS: rhs,
    Unit,
    Bounded: !!bounded, // for DateDiff -> true
  },
});

//#endregion

//#region StringFnExpr
type IStringFnExpr<S extends ExpressionSubTypes<ExpressionTypes.StringFn>> =
  IExpression<ExpressionTypes.StringFn, S>;

export interface IPositionExprPart {
  Haystack: IExpression;
  Needle: IExpression;
}

export interface IConcatExprPart {
  Items: IExpression[];
  Separator?: string;
  FilterEmpty?: boolean;
}

export interface IJSONExtractExprPart {
  JSON: IExpression;
  Keys: IExpression[];
}

export interface IRegexpExtractExprPart {
  Haystack: IExpression;
  Pattern: string;
  Index?: number;
}

export interface IReplaceExprPart {
  Haystack: IExpression;
  Pattern: string;
  Replacement: string;
}

export interface IReplaceRegexpExprPart {
  Haystack: IExpression;
  Pattern: string;
  Replacement: string;
}

export const newPositionExpr = (haystack: IExpression, needle: IExpression): IStringFnExpr<StringFnExprTypes.Position> => ({
  Type: ExpressionTypes.StringFn,
  SubType: StringFnExprTypes.Position,
  Part: { Haystack: haystack, Needle: needle },
});

export const newConcatExpr = (
  expressions: IExpression[],
  separator: string = '',
  filterEmpty: boolean = false,
): IStringFnExpr<StringFnExprTypes.Concat> => ({
  Type: ExpressionTypes.StringFn,
  SubType: StringFnExprTypes.Concat,
  Part: { Items: expressions, Separator: separator, FilterEmpty: filterEmpty },
});

export const newRegexpExtractExpr = (haystack: IExpression, pattern: string, index?: number): IStringFnExpr<StringFnExprTypes.RegexpExtract> => ({
  Type: ExpressionTypes.StringFn,
  SubType: StringFnExprTypes.RegexpExtract,
  Part: { Haystack: haystack, Pattern: pattern, Index: index },
});

export const newReplaceExpr = (haystack: IExpression, pattern: string, replacement: string): IStringFnExpr<StringFnExprTypes.Replace> => ({
  Type: ExpressionTypes.StringFn,
  SubType: StringFnExprTypes.Replace,
  Part: { Haystack: haystack, Pattern: pattern, Replacement: replacement },
});

export const newReplaceRegexpExpr = (haystack: IExpression, pattern: string, replacement: string): IStringFnExpr<StringFnExprTypes.ReplaceRegexp> => ({
  Type: ExpressionTypes.StringFn,
  SubType: StringFnExprTypes.ReplaceRegexp,
  Part: { Haystack: haystack, Pattern: pattern, Replacement: replacement },
});
//#endregion

//#region JSONFnExpr
type IJSONFnExpr<S extends ExpressionSubTypes<ExpressionTypes.JSONFn>> =
  IExpression<ExpressionTypes.JSONFn, S>;

export const newJSONExtractExpr = (json: IExpression, keys: IExpression[]): IJSONFnExpr<JSONFnExprTypes.JSONExtract> => ({
  Type: ExpressionTypes.JSONFn,
  SubType: JSONFnExprTypes.JSONExtract,
  Part: { JSON: json, Keys: keys },
});
//#endregion

export interface IArrayJoinExprPart { Array: IExpression }
export const newArrayJoinExpr = (column: IExpression): IExpression<ExpressionTypes.ArrayFn, ArrayFnExprTypes.ArrayJoinExpr> => ({
  Type: ExpressionTypes.ArrayFn,
  SubType: ArrayFnExprTypes.ArrayJoinExpr,
  Part: { Array: column },
});

export const newArrayStringConcatExpr = (column: IExpression): IExpression<ExpressionTypes.ArrayFn, ArrayFnExprTypes> => ({
  Type: ExpressionTypes.ArrayFn,
  SubType: ArrayFnExprTypes.ArrayStringConcatExpr,
  Part: { Array: column },
});

export interface IArrayIndexOfExprPart { Array: IExpression; Element: IExpression }
export const newArrayIndexOfExpr = (array: IExpression, element: IExpression): IExpression<ExpressionTypes.ArrayFn, ArrayFnExprTypes.ArrayIndexOfExpr> => ({
  Type: ExpressionTypes.ArrayFn,
  SubType: ArrayFnExprTypes.ArrayIndexOfExpr,
  Part: { Array: array, Element: element },
});

export interface IConfigExprPart {
  Namespace: ConfigExprNamespace; // defines where to look for config (user, tenant settings, etc.)
  Key: TenantSettingKey; // defines the exact config's value to substitute in query
}
export const newConfigExpr = (namespace: ConfigExprNamespace, key: TenantSettingKey): IExpression<ExpressionTypes.Config> => ({
  Type: ExpressionTypes.Config,
  SubType: null,
  Part: {
    Namespace: namespace, Key: key,
  },
});

export const newIfExpr = (
  conditionExpr: IExpression,
  thenPart: IExpression,
  elsePart: IExpression,
): IExpression => ({
  Type: ExpressionTypes.Condition,
  SubType: ConditionExprTypes.If,
  Part: {
    Condition: conditionExpr,
    ThenPart: thenPart,
    ElsePart: elsePart,
  },
});

//#region CastFnExpr
type ICastFnExpr<S extends ExpressionSubTypes<ExpressionTypes.CastFn>> =
  IExpression<ExpressionTypes.CastFn, S>;

export interface ICastExprPart {
  Expression: IExpression;
  TargetType: string;
}

export interface IAccurateCastOrDefaultExprPart {
  Expression: IExpression;
  TargetType: string;
  DefaultValue?: IExpression;
}

// DefaultValue key is omitted (not set to undefined) when absent, keeping the JSON shape clean.
const buildCastFnExpr = <S extends ExpressionSubTypes<ExpressionTypes.CastFn>>(
  subType: S,
  expr: IExpression,
  targetType: string,
  defaultValue?: IExpression,
): ICastFnExpr<S> => ({
    Type: ExpressionTypes.CastFn,
    SubType: subType,
    Part: { Expression: expr, TargetType: targetType, ...(defaultValue !== undefined ? { DefaultValue: defaultValue } : {}) },
  } as ICastFnExpr<S>);

export const newCastExpr = (expr: IExpression, targetType: string): ICastFnExpr<CastFnExprTypes.Cast> => (
  buildCastFnExpr(CastFnExprTypes.Cast, expr, targetType)
);

export const newAccurateCastOrNullExpr = (expr: IExpression, targetType: string): ICastFnExpr<CastFnExprTypes.AccurateCastOrNull> => (
  buildCastFnExpr(CastFnExprTypes.AccurateCastOrNull, expr, targetType)
);

export const newAccurateCastOrDefaultExpr = (expr: IExpression, targetType: string, defaultValue?: IExpression): ICastFnExpr<CastFnExprTypes.AccurateCastOrDefault> => (
  buildCastFnExpr(CastFnExprTypes.AccurateCastOrDefault, expr, targetType, defaultValue)
);
//#endregion

//#region Type Guards

export type IAggExprGuarded =
  IAggExpr<AggExprTypes.Sum> | IAggExpr<AggExprTypes.SumIf>
  | IAggExpr<AggExprTypes.Max> | IAggExpr<AggExprTypes.MaxIf>
  | IAggExpr<AggExprTypes.Min> | IAggExpr<AggExprTypes.MinIf>
  | IAggExpr<AggExprTypes.Avg> | IAggExpr<AggExprTypes.AvgIf>
  | IAggExpr<AggExprTypes.Count> | IAggExpr<AggExprTypes.CountIf>
  | IAggExpr<AggExprTypes.CountDistinct> | IAggExpr<AggExprTypes.CountDistinctIf>
  | IAggExpr<AggExprTypes.ArgMax>
  | IAggExpr<AggExprTypes.ArgMin>
  | IAggExpr<AggExprTypes.ArgMaxIf>
  | IAggExpr<AggExprTypes.ArgMinIf>
  | IAggExpr<AggExprTypes.Any>
  | IAggExpr<AggExprTypes.AnyHeavy>
  | IAggExpr<AggExprTypes.AnyLast>
  | IAggExpr<AggExprTypes.GroupConcat>
  | IAggExpr<AggExprTypes.GroupConcatDistinct>;

export type IWindowFnExprGuarded =
  IWindowFnExpr<WindowFnExprTypes.RowNumber>
  | IWindowFnExpr<WindowFnExprTypes.Rank>
  | IWindowFnExpr<WindowFnExprTypes.FirstValue>
  | IWindowFnExpr<WindowFnExprTypes.LastValue>
  | IWindowFnExpr<WindowFnExprTypes.Lead>
  | IWindowFnExpr<WindowFnExprTypes.Lag>;

export type IArithmeticExprGuarded =
  IArithmeticExpr<ArithmeticExprTypes.Plus> | IArithmeticExpr<ArithmeticExprTypes.Minus>
  | IArithmeticExpr<ArithmeticExprTypes.Multiply> | IArithmeticExpr<ArithmeticExprTypes.Divide>;

export type ILogicalExprGuarded =
  ILogicalExpr<LogicalExprTypes.And> | ILogicalExpr<LogicalExprTypes.Or>
  | ILogicalExpr<LogicalExprTypes.Not> | ILogicalExpr<LogicalExprTypes.IsNull>;

export type IExpressionGuarded =
  IExpression<ExpressionTypes.Column> | IExpression<ExpressionTypes.Constant>
  | IExpression<ExpressionTypes.CustomFilterVariable>
  | IExpression<ExpressionTypes.Config>
  | IExpression<ExpressionTypes.Keyword, KeywordExprTypes.Null>
  | IAggExprGuarded
  | IArithmeticExprGuarded
  | ICompExpr<CompExprTypes>
  | ILogicalExprGuarded
  | IDateExpr<DateBinningFormatters> | IDateExpr<DateFnExprTypes> | IDateExpr<DateDiffFnExprTypes>
  | IStringFnExpr<StringFnExprTypes.Position>
  | IStringFnExpr<StringFnExprTypes.Concat>
  | IStringFnExpr<StringFnExprTypes.RegexpExtract>
  | IStringFnExpr<StringFnExprTypes.Replace>
  | IStringFnExpr<StringFnExprTypes.ReplaceRegexp>
  | IJSONFnExpr<JSONFnExprTypes.JSONExtract>
  | IExpression<ExpressionTypes.ArrayFn, ArrayFnExprTypes.ArrayJoinExpr>
  | IExpression<ExpressionTypes.ArrayFn, ArrayFnExprTypes.ArrayIndexOfExpr>
  | IExpression<ExpressionTypes.Condition>
  | IWindowFnExprGuarded
  | ICastFnExpr<CastFnExprTypes.Cast>
  | ICastFnExpr<CastFnExprTypes.AccurateCastOrNull>
  | ICastFnExpr<CastFnExprTypes.AccurateCastOrDefault>;

export const isColumn = (expr: IExpression): expr is IExpression<ExpressionTypes.Column> => expr.Type === ExpressionTypes.Column;
export const isConstant = (expr: IExpression): expr is IExpression<ExpressionTypes.Constant> => expr.Type === ExpressionTypes.Constant;
export const isCustomFilterVariable = (expr: IExpression): expr is IExpression<ExpressionTypes.CustomFilterVariable> => expr.Type === ExpressionTypes.CustomFilterVariable;
export const isConfig = (expr: IExpression): expr is IExpression<ExpressionTypes.Config> => expr.Type === ExpressionTypes.Config;
export const isKeyword = (expr: IExpression): expr is IExpression<ExpressionTypes.Keyword> => expr.Type === ExpressionTypes.Keyword;
export const isNull = (expr: IExpression): expr is IExpression<ExpressionTypes.Keyword, KeywordExprTypes.Null> => isKeyword(expr) && expr.SubType === KeywordExprTypes.Null;

// #region aggregate utility functions
export const isAgg = (expr: IExpression): expr is IAggExpr<AggExprTypes> => expr?.Type === ExpressionTypes.Aggregation;
export const isSum = (expr: IExpression): expr is IAggExpr<AggExprTypes.Sum> => isAgg(expr) && expr.SubType === AggExprTypes.Sum;
export const isSumIf = (expr: IExpression): expr is IAggExpr<AggExprTypes.SumIf> => isAgg(expr) && expr.SubType === AggExprTypes.SumIf;
export const isMax = (expr: IExpression): expr is IAggExpr<AggExprTypes.Max> => isAgg(expr) && expr.SubType === AggExprTypes.Max;
export const isMaxIf = (expr: IExpression): expr is IAggExpr<AggExprTypes.MaxIf> => isAgg(expr) && expr.SubType === AggExprTypes.MaxIf;
export const isMin = (expr: IExpression): expr is IAggExpr<AggExprTypes.Min> => isAgg(expr) && expr.SubType === AggExprTypes.Min;
export const isMinIf = (expr: IExpression): expr is IAggExpr<AggExprTypes.MinIf> => isAgg(expr) && expr.SubType === AggExprTypes.MinIf;
export const isAvg = (expr: IExpression): expr is IAggExpr<AggExprTypes.Avg> => isAgg(expr) && expr.SubType === AggExprTypes.Avg;
export const isAvgIf = (expr: IExpression): expr is IAggExpr<AggExprTypes.AvgIf> => isAgg(expr) && expr.SubType === AggExprTypes.AvgIf;
export const isCount = (expr: IExpression): expr is IAggExpr<AggExprTypes.Count> => isAgg(expr) && expr.SubType === AggExprTypes.Count;
export const isCountIf = (expr: IExpression): expr is IAggExpr<AggExprTypes.CountIf> => isAgg(expr) && expr.SubType === AggExprTypes.CountIf;
export const isCountDistinct = (expr: IExpression): expr is IAggExpr<AggExprTypes.CountDistinct> => isAgg(expr) && expr.SubType === AggExprTypes.CountDistinct;
export const isCountDistinctIf = (expr: IExpression): expr is IAggExpr<AggExprTypes.CountDistinctIf> => isAgg(expr) && expr.SubType === AggExprTypes.CountDistinctIf;
export const isArgMax = (expr: IExpression): expr is IAggExpr<AggExprTypes.ArgMax> => isAgg(expr) && expr.SubType === AggExprTypes.ArgMax;
export const isArgMin = (expr: IExpression): expr is IAggExpr<AggExprTypes.ArgMin> => isAgg(expr) && expr.SubType === AggExprTypes.ArgMin;
export const isArgMaxIf = (expr: IExpression): expr is IExpression<ExpressionTypes.Aggregation, AggExprTypes.ArgMaxIf> => isAgg(expr) && expr.SubType === AggExprTypes.ArgMaxIf;
export const isArgMinIf = (expr: IExpression): expr is IExpression<ExpressionTypes.Aggregation, AggExprTypes.ArgMinIf> => isAgg(expr) && expr.SubType === AggExprTypes.ArgMinIf;
export const isAny = (expr: IExpression): expr is IAggExpr<AggExprTypes.Any> => isAgg(expr) && expr.SubType === AggExprTypes.Any;
export const isAnyHeavy = (expr: IExpression): expr is IAggExpr<AggExprTypes.AnyHeavy> => isAgg(expr) && expr.SubType === AggExprTypes.AnyHeavy;
export const isAnyLast = (expr: IExpression): expr is IAggExpr<AggExprTypes.AnyLast> => isAgg(expr) && expr.SubType === AggExprTypes.AnyLast;
export const isGroupConcat = (expr: IExpression): expr is IAggExpr<AggExprTypes.GroupConcat> => isAgg(expr) && expr.SubType === AggExprTypes.GroupConcat;
export const isGroupConcatDistinct = (expr: IExpression): expr is IAggExpr<AggExprTypes.GroupConcatDistinct> => isAgg(expr) && expr.SubType === AggExprTypes.GroupConcatDistinct;

// #endregion

// #region window function utility functions
export const isWindowFn = (expr: IExpression): expr is IWindowFnExpr<WindowFnExprTypes> => expr?.Type === ExpressionTypes.WindowFn;
export const isRowNumber = (expr: IExpression): expr is IWindowFnExpr<WindowFnExprTypes.RowNumber> => isWindowFn(expr) && expr.SubType === WindowFnExprTypes.RowNumber;
export const isRank = (expr: IExpression): expr is IWindowFnExpr<WindowFnExprTypes.Rank> => isWindowFn(expr) && expr.SubType === WindowFnExprTypes.Rank;
export const isFirstValue = (expr: IExpression): expr is IWindowFnExpr<WindowFnExprTypes.FirstValue> => isWindowFn(expr) && expr.SubType === WindowFnExprTypes.FirstValue;
export const isLastValue = (expr: IExpression): expr is IWindowFnExpr<WindowFnExprTypes.LastValue> => isWindowFn(expr) && expr.SubType === WindowFnExprTypes.LastValue;
export const isLead = (expr: IExpression): expr is IWindowFnExpr<WindowFnExprTypes.Lead> => isWindowFn(expr) && expr.SubType === WindowFnExprTypes.Lead;
export const isLag = (expr: IExpression): expr is IWindowFnExpr<WindowFnExprTypes.Lag> => isWindowFn(expr) && expr.SubType === WindowFnExprTypes.Lag;
// #endregion

// #region arithmetic utility functions
export const isArithmetic = (expr: IExpression): expr is IArithmeticExpr => expr.Type === ExpressionTypes.Arithmetic;
export const isPlus = (expr: IExpression): expr is IArithmeticExpr<ArithmeticExprTypes.Plus> => isArithmetic(expr) && expr.SubType === ArithmeticExprTypes.Plus;
export const isMinus = (expr: IExpression): expr is IArithmeticExpr<ArithmeticExprTypes.Minus> => isArithmetic(expr) && expr.SubType === ArithmeticExprTypes.Minus;
export const isMultiply = (expr: IExpression): expr is IArithmeticExpr<ArithmeticExprTypes.Multiply> => isArithmetic(expr) && expr.SubType === ArithmeticExprTypes.Multiply;
export const isDivide = (expr: IExpression): expr is IArithmeticExpr<ArithmeticExprTypes.Divide> => isArithmetic(expr) && expr.SubType === ArithmeticExprTypes.Divide;
// #endregion

// #region comparison utility functions
export const isComp = (expr: IExpression): expr is ICompExpr<CompExprTypes> => expr.Type === ExpressionTypes.Comparison;
export const isLogical = (expr: IExpression): expr is IExpression<ExpressionTypes.Logical> => expr.Type === ExpressionTypes.Logical;
export const isAnd = (expr: IExpression): expr is IExpression<ExpressionTypes.Logical, LogicalExprTypes.And> => isLogical(expr) && expr.SubType === LogicalExprTypes.And;
export const isOr = (expr: IExpression): expr is IExpression<ExpressionTypes.Logical, LogicalExprTypes.Or> => isLogical(expr) && expr.SubType === LogicalExprTypes.Or;
export const isNot = (expr: IExpression): expr is IExpression<ExpressionTypes.Logical, LogicalExprTypes.Not> => isLogical(expr) && expr.SubType === LogicalExprTypes.Not;
export const isIsNull = (expr: IExpression): expr is IExpression<ExpressionTypes.Logical, LogicalExprTypes.IsNull> => isLogical(expr) && expr.SubType === LogicalExprTypes.IsNull;
// #endregion

// #region date utility functions
export const isDate = (expr: IExpression): expr is IDateExpr<DateExprTypes> => expr.Type === ExpressionTypes.Date;
export const isDateFn = (expr: IExpression): expr is IDateExpr<DateFnExprTypes> => isDate(expr) && (expr.SubType === DateFnExprTypes.Subtract || expr.SubType === DateFnExprTypes.Add || expr.SubType === DateFnExprTypes.CurrentDateTime);
export const isDateDiffFn = (expr: IExpression): expr is IDateExpr<DateDiffFnExprTypes> => isDate(expr) && (expr.SubType === DateDiffFnExprTypes.DifferenceExpr);
// #endregion

// #region string utility functions
export const isStringFn = (expr: IExpression): expr is IStringFnExpr<StringFnExprTypes> => expr.Type === ExpressionTypes.StringFn;
export const isPosition = (expr: IExpression): expr is IStringFnExpr<StringFnExprTypes.Position> => isStringFn(expr) && expr.SubType === StringFnExprTypes.Position;
export const isConcat = (expr: IExpression): expr is IStringFnExpr<StringFnExprTypes.Concat> => isStringFn(expr) && expr.SubType === StringFnExprTypes.Concat;
export const isRegexpExtract = (expr: IExpression): expr is IStringFnExpr<StringFnExprTypes.RegexpExtract> => isStringFn(expr) && expr.SubType === StringFnExprTypes.RegexpExtract;
export const isReplace = (expr: IExpression): expr is IStringFnExpr<StringFnExprTypes.Replace> => isStringFn(expr) && expr.SubType === StringFnExprTypes.Replace;
export const isReplaceRegexp = (expr: IExpression): expr is IStringFnExpr<StringFnExprTypes.ReplaceRegexp> => isStringFn(expr) && expr.SubType === StringFnExprTypes.ReplaceRegexp;
// #endregion

// #region json utility functions
export const isJSONFn = (expr: IExpression): expr is IJSONFnExpr<JSONFnExprTypes> => expr.Type === ExpressionTypes.JSONFn;
export const isJSONExtract = (expr: IExpression): expr is IJSONFnExpr<JSONFnExprTypes.JSONExtract> => isJSONFn(expr) && expr.SubType === JSONFnExprTypes.JSONExtract;
// #endregion

// #region array utility functions
export const isArrayFn = (expr: IExpression): expr is IExpression<ExpressionTypes.ArrayFn> => expr.Type === ExpressionTypes.ArrayFn;
export const isArrayJoinFn = (expr: IExpression): expr is IExpression<ExpressionTypes.ArrayFn, ArrayFnExprTypes.ArrayJoinExpr> => isArrayFn(expr) && expr.SubType === ArrayFnExprTypes.ArrayJoinExpr;
export const isArrayIndexOfFn = (expr: IExpression): expr is IExpression<ExpressionTypes.ArrayFn, ArrayFnExprTypes.ArrayIndexOfExpr> => isArrayFn(expr) && expr.SubType === ArrayFnExprTypes.ArrayIndexOfExpr;
//#endregion

// #region cast utility functions
export const isCastFn = (expr: IExpression): expr is ICastFnExpr<CastFnExprTypes> => expr.Type === ExpressionTypes.CastFn;
export const isCast = (expr: IExpression): expr is ICastFnExpr<CastFnExprTypes.Cast> => isCastFn(expr) && expr.SubType === CastFnExprTypes.Cast;
export const isAccurateCastOrNull = (expr: IExpression): expr is ICastFnExpr<CastFnExprTypes.AccurateCastOrNull> => isCastFn(expr) && expr.SubType === CastFnExprTypes.AccurateCastOrNull;
export const isAccurateCastOrDefault = (expr: IExpression): expr is ICastFnExpr<CastFnExprTypes.AccurateCastOrDefault> => isCastFn(expr) && expr.SubType === CastFnExprTypes.AccurateCastOrDefault;
// #endregion

// #region condition utility functions
export const isIf = (expr: IExpression): expr is IExpression<ExpressionTypes.Condition> => expr.Type === ExpressionTypes.Condition && (expr.SubType === ConditionExprTypes.If);
// #endregion
